import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { musteriApi } from './api';
import MusteriSorular from './MusteriSorular';
import MusteriSepet from './MusteriSepet';
import MusteriSiparisler from './MusteriSiparisler';
import MusteriSecimOzeti from './MusteriSecimOzeti';

// ============================================================================
// MÜŞTERİ PANELİ (2026-10-26, kullanıcı isteği): müşteri hesabı onaylı + satılmamış soruları süzer,
// sepete ekler, "Sipariş oluştur" ile soru numarası listesi alır. Sipariş soruları başka müşterilere
// kapatır; yönetici "Satışa işle" deyince müşterinin yayınevine satış kaydı olur.
// Ana paneldeki soru SAYILARI burada YOK (kullanıcı isteği) — sayfa yalnız sorunun kendisini gösterir.
// Kök ayrımı index.js/KokUygulama: müşteri App'i hiç yüklemez (sayaç, ders özeti, websocket çalışmaz).
// ============================================================================
// Sorular sekmesi geniş (kitapçık sayfası + seçim özeti yan yana); sepet/siparişler eski genişlikte.
const GENISLIK = (sekme) => (sekme === 'sorular' ? 'max-w-[1400px]' : 'max-w-5xl');
const SEKME = (aktif) => `px-3 py-1.5 rounded-md text-sm font-semibold ${aktif ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`;

export default function MusteriPaneli() {
  const { kullanici, cikisYap, authFetch } = useAuth();
  const api = useMemo(() => musteriApi(authFetch), [authFetch]);
  const [sekme, setSekme] = useState('sorular');
  const [profil, setProfil] = useState(null);
  const [sepet, setSepet] = useState(() => new Set());
  const [mesgul, setMesgul] = useState(false);
  const [bildirim, setBildirim] = useState(null);   // { tip, metin }
  const [yeniSiparis, setYeniSiparis] = useState(null);
  // Seçim özeti kalemleri (ders + öğrenme çıktısı); sepet kümesiyle süzülür (sepet sekmesindeki çıkarma).
  const [kalemler, setKalemler] = useState([]);

  const sepetOku = useCallback(() => api.sepet().then((d) => {
    const k = d.kalemler || [];
    setKalemler(k);
    setSepet(new Set(k.map((x) => x.soru_no)));
  }), [api]);

  useEffect(() => {
    let iptal = false;
    api.profil().then((p) => { if (!iptal) setProfil(p); }).catch(() => {});
    api.sepet().then((d) => { if (!iptal) { setKalemler(d.kalemler || []); setSepet(new Set((d.kalemler || []).map((k) => k.soru_no))); } }).catch(() => {});
    return () => { iptal = true; };
  }, [api]);

  const sepetDegisti = useCallback((nolar) => setSepet(new Set(nolar)), []);
  const bildir = (tip, metin) => setBildirim({ tip, metin });

  const ekle = async (nolar) => {
    setMesgul(true); setBildirim(null);
    try {
      const d = await api.sepeteEkle(nolar);
      setSepet((o) => new Set([...o, ...(d.eklenen || []), ...(d.zaten_sepette || [])]));
      const parca = [];
      if ((d.eklenen || []).length) parca.push(`${d.eklenen.length} soru sepete eklendi`);
      if ((d.grup_eklenen || []).length) parca.push(`bağlam grubu birlikte verildiği için ${d.grup_eklenen.map((n) => `#${n}`).join(', ')} de eklendi`);
      if ((d.red || []).length) parca.push(`eklenemedi: ${d.red.map((r) => `#${r.public_no} (${r.neden})`).join('; ')}`);
      bildir((d.red || []).length ? 'uyari' : 'bilgi', parca.join(' — ') || 'Seçilen sorular zaten sepette.');
      await sepetOku().catch(() => {});
    } catch (e) {
      bildir('hata', e.message);
    }
    setMesgul(false);
  };
  const cikar = async (no) => {
    setMesgul(true); setBildirim(null);
    try {
      const d = await api.sepettenCikar(no);
      setSepet((o) => { const n = new Set(o); (d.cikarilan || [no]).forEach((x) => n.delete(x)); return n; });
      if ((d.cikarilan || []).length > 1) bildir('bilgi', `Bağlam grubu birlikte çıkarıldı: ${d.cikarilan.map((n) => `#${n}`).join(', ')}`);
      setKalemler((o) => o.filter((k) => !(d.cikarilan || [no]).includes(k.soru_no)));
    } catch (e) {
      bildir('hata', e.message);
    }
    setMesgul(false);
  };
  const sepetDugmesi = (soru) => (sepet.has(soru.soru_no) ? cikar(soru.soru_no) : ekle([soru.soru_no]));

  const siparisOldu = (d) => {
    setYeniSiparis(d.siparis_no);
    setSekme('siparisler');
    bildir('bilgi', `${d.siparis_no} numaralı sipariş oluşturuldu (${(d.numaralar || []).length} soru). Sorular sizin için ayrıldı; işlendiğinde durum "İşlendi" olur.`);
  };

  const ad = (profil && profil.ad) || (kullanici && kullanici.ad) || '';
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b border-gray-200">
        <div className={`${GENISLIK(sekme)} mx-auto px-4 py-3 flex flex-wrap items-center gap-3`}>
          <div className="mr-auto">
            <div className="text-base font-bold text-gray-800">Soru Seçim Paneli</div>
            <div className="text-xs text-gray-500" data-musteri-kimlik>
              {ad}{profil && profil.yayinevi_ad ? ` · ${profil.yayinevi_ad}` : ''}
            </div>
          </div>
          <nav className="flex gap-1" aria-label="Müşteri sekmeleri">
            <button type="button" data-sekme="sorular" aria-current={sekme === 'sorular' ? 'page' : undefined} onClick={() => setSekme('sorular')} className={SEKME(sekme === 'sorular')}>Sorular</button>
            <button type="button" data-sekme="sepet" aria-current={sekme === 'sepet' ? 'page' : undefined} onClick={() => setSekme('sepet')} className={SEKME(sekme === 'sepet')}>
              Sepet{sepet.size ? ` (${sepet.size})` : ''}
            </button>
            <button type="button" data-sekme="siparisler" aria-current={sekme === 'siparisler' ? 'page' : undefined} onClick={() => setSekme('siparisler')} className={SEKME(sekme === 'siparisler')}>Siparişlerim</button>
          </nav>
          <button type="button" onClick={cikisYap} className="px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-600 hover:bg-gray-50">Çıkış</button>
        </div>
      </header>
      <main className={`${GENISLIK(sekme)} mx-auto px-4 py-4 space-y-3`}>
        {bildirim && (
          <div data-bildirim={bildirim.tip} className={`p-3 rounded-lg border text-sm flex gap-3 ${bildirim.tip === 'hata'
            ? 'border-red-300 bg-red-50 text-red-700' : bildirim.tip === 'uyari' ? 'border-orange-300 bg-orange-50 text-orange-900' : 'border-green-300 bg-green-50 text-green-800'}`}>
            <span className="flex-1">{bildirim.metin}</span>
            <button type="button" aria-label="Kapat" onClick={() => setBildirim(null)} className="text-xs opacity-70 hover:opacity-100">✕</button>
          </div>
        )}
        {sekme === 'sorular' && (
          // Seçim özeti sağda, kaydırınca birlikte kayar (sticky); dar ekranda listenin üstünde.
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
            <div className="order-last min-w-0 lg:order-none">
              <MusteriSorular api={api} sepet={sepet} onSepet={sepetDugmesi} onTopluEkle={ekle} mesgul={mesgul}
                izinliDersler={(profil && profil.izinli_dersler) || (kullanici && kullanici.izinli_dersler) || null} />
            </div>
            <MusteriSecimOzeti kalemler={kalemler.filter((k) => sepet.has(k.soru_no))} mesgul={mesgul}
              onCikar={cikar} onSepeteGit={() => setSekme('sepet')} />
          </div>
        )}
        {sekme === 'sepet' && <MusteriSepet api={api} sepetDegisti={sepetDegisti} onSiparis={siparisOldu} />}
        {sekme === 'siparisler' && <MusteriSiparisler api={api} vurgu={yeniSiparis} />}
      </main>
    </div>
  );
}
